import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Star } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { useAuth } from '../context/AuthContext';
import { purchaseSubscription } from '../services/subscriptionService';

const Pricing = () => {
  const navigate = useNavigate();
  const { isLoggedIn, setSubscriptionStatus } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const ctaText = isLoggedIn ? 'GET STARTED' : 'SIGN UP';

  const handlePurchase = async (plan_name: string, price: number) => {
    if (!isLoggedIn) {
      navigate('/register');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    setMessage('');
    // setLoadingPlan(plan_name);
    // try {
    //   const response = await purchaseSubscription(token, { plan_name, price });
    //   setMessage(response.message || 'Subscription purchased successfully.');
    //   setSubscriptionStatus(true);
    //   navigate('/account');
    // } catch (err: any) {
    //   setMessage(err.message || 'Failed to purchase subscription.');
    // } finally {
    //   setLoadingPlan(null);
    // }
  };

  return (
    <>
      <Header />

      <main className="font-sans text-zinc-900 ">
        
        {/* ========================================
            UNIFIED HERO, PRICING & CTA SECTION
            ======================================== */}
        <section className="relative overflow-hidden bg-white pt-24 md:pt-32 pb-24">
          
          {/* Continuous Grid Background */}
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          ></div>

          {/* Subtle gradient to blend the bottom smoothly */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none"></div>
          
          {/* Ambient Glows distributed across the whole section */}
       

          {/* --- HERO CONTENT --- */}
          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12 text-center mb-16 md:mb-24">
            <h1 className="mb-6 text-5xl font-black tracking-tighter text-zinc-950 md:text-6xl">
              Simple, transparent pricing
            </h1>
            
            <p className="mx-auto max-w-2xl text-lg font-medium text-zinc-600 leading-relaxed">
              Choose the plan that best fits your learning goals. Upgrade, downgrade, or cancel anytime.
            </p>
            {message && (
              <div className="mt-6 inline-block rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm font-medium text-zinc-700">
                {message}
              </div>
            )}
          </div>

          {/* --- PRICING CARDS --- */}
          <div className="relative z-10 mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-3 lg:px-12 items-center mb-32">
            
            {/* TIER 1: Basic (Light) */}
            <article className="rounded-[2rem] border border-zinc-200 bg-white p-8 md:p-10 shadow-xl transition-transform hover:-translate-y-1 duration-300 flex flex-col h-full text-center">
              <h3 className="text-xl font-black text-zinc-950 mb-6">Basic</h3>
              
              <h2 className="mb-8 text-5xl font-black text-zinc-950 flex items-end justify-center gap-1 border-b border-zinc-100 pb-8">
               €29
                <span className="text-sm font-bold text-zinc-500 mb-2 tracking-normal">/Per Month</span>
              </h2>
              
              <ul className="mb-10 space-y-5 text-sm font-bold text-zinc-600 flex-1 text-left">
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Access to Basic Courses</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Access on any device</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Cancel Anytime</li>
              </ul>
              
              <button
                type="button"
                onClick={() => handlePurchase('Basic Monthly', 29)}
                disabled={loadingPlan === 'Basic Monthly'}
                className="hover:bg-white hover:text-zinc-950 hover:border-[#09090B] border border-[#09090B] block rounded-xl bg-[#09090B] text-white px-5 py-4 text-center text-sm font-black uppercase tracking-widest transition-all  shadow-md hover:shadow-lg mt-auto"
              >
                {loadingPlan === 'Basic Monthly' ? 'PROCESSING...' : ctaText}
              </button>
            </article>

            {/* TIER 2: Premium (Dark / Highlighted) */}
            <article className="relative rounded-[2rem] border-2 border-[#f59e0b] bg-zinc-950 p-8 md:p-12 shadow-[0_20px_50px_rgba(245,158,11,0.2)] transform md:-translate-y-4 z-10 flex flex-col h-full text-center">
              {/* Highlight Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-[#f59e0b] px-4 py-1.5 text-xs font-black uppercase tracking-widest text-zinc-950 shadow-md flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-zinc-950" /> Most Popular
              </div>

              <h3 className="text-2xl font-black text-[#f59e0b] mb-6">Premium</h3>
              
              <h2 className="mb-8 text-6xl font-black text-white flex items-end justify-center gap-1 border-b border-zinc-800 pb-8">
              €99
                <span className="text-sm font-medium text-zinc-400 mb-2 tracking-normal">/Per Month</span>
              </h2>
              
              <ul className="mb-10 space-y-5 text-sm font-bold text-white flex-1 text-left">
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#f59e0b] shrink-0" /> Access to All Courses</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#f59e0b] shrink-0" /> Access On Any Device</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#f59e0b] shrink-0" /> Unlimited Downloads</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#f59e0b] shrink-0" /> Unlimited Transcripts</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#f59e0b] shrink-0" /> Cancel Anytime</li>
              </ul>
              
              <button
                type="button"
                onClick={() => handlePurchase('Pro Monthly', 499)}
                disabled={loadingPlan === 'Pro Monthly'}
                className="block rounded-xl bg-[#f59e0b] px-5 py-4 text-center text-sm font-black uppercase tracking-widest text-zinc-950 transition-all  shadow-[0_0_20px_rgba(163,230,53,0.3)] hover:-translate-y-1 mt-auto"
              >
                {loadingPlan === 'Pro Monthly' ? 'PROCESSING...' : ctaText}
              </button>
            </article>

            {/* TIER 3: Standard (Light) */}
            <article className="rounded-[2rem] border border-zinc-200 bg-white p-8 md:p-10 shadow-xl transition-transform hover:-translate-y-1 duration-300 flex flex-col h-full text-center">
              <h3 className="text-xl font-black text-zinc-950 mb-6">Standard</h3>
              
              <h2 className="mb-8 text-5xl font-black text-zinc-950 flex items-end justify-center gap-1 border-b border-zinc-100 pb-8">
                €59
                <span className="text-sm font-bold text-zinc-500 mb-2 tracking-normal">/Per Month</span>
              </h2>
              
              <ul className="mb-10 space-y-5 text-sm font-bold text-zinc-600 flex-1 text-left">
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Access to Advanced Courses</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Access on any device</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Limited Downloads</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Limited Transcripts</li>
                <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-zinc-900 shrink-0" /> Cancel Anytime</li>
              </ul>
              
              <button
                type="button"
                onClick={() => handlePurchase('Standard Monthly', 59)}
                disabled={loadingPlan === 'Standard Monthly'}
                className="hover:bg-white hover:text-zinc-950 hover:border-[#09090B] border border-[#09090B] block rounded-xl bg-[#09090B] text-white px-5 py-4 text-center text-sm font-black uppercase tracking-widest transition-all  shadow-md hover:shadow-lg mt-auto"
              >
                {loadingPlan === 'Standard Monthly' ? 'PROCESSING...' : ctaText}
              </button>
            </article>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default Pricing; 